import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  HStack,
  Icon,
  Radio,
  Spacer,
  Text,
} from "@chakra-ui/react";
import parseISO from "date-fns/parseISO";
import { groupBy, uniqBy } from "lodash";
import React, { useEffect } from "react";
import { HiCurrencyDollar } from "react-icons/hi";
import useSWR from "swr";
import BottomAction from "../components/BottomAction";
import { useRegistration } from "../context/Registration";
import { Course, OfferingAndSessions } from "../models";
import { camelify, parseSchedule, displayOrderPredicate, getParagraphs } from "../util";

type SelectCallback = (offering: OfferingAndSessions) => any;

type CardPropTypes = {
  offering: OfferingAndSessions;
  onSelect: SelectCallback;
  isSelected: (id: number) => boolean;
};

const OfferingCard = ({ offering, isSelected, onSelect }: CardPropTypes) => {
  const schedules = offering.schedules.map(parseSchedule);
  const onClick = () => onSelect(offering);
  const sel = isSelected(offering.id);
  return (
    <Flex
      background="white"
      direction="column"
      alignItems="stretch"
      borderRadius="md"
      boxShadow="0px 4px 4px rgba(0, 0, 0, 0.06)"
      cursor="pointer"
      onClick={onClick}
      _hover={{
        borderColor: 'gray.400',
      }}
      _active={{
        borderColor: 'gray.400',
      }}
      borderWidth="1px"
      p={4}
    >
      <Flex
        direction="column"
        justifyContent="center"
        alignItems="center"
        position="relative"
        minH={12}
      >
        <Radio
          size="lg"
          cursor="pointer"
          position="absolute"
          top="0px"
          right="0px"
          onChange={() => onClick()}
          onClick={(e) => e.preventDefault()}
          colorScheme="orange"
          isChecked={sel}
        />
        {schedules.map((sch) => (
          <Text key={sch}>{sch}</Text>
        ))}
      </Flex>
    </Flex>
  );
};

type CourseSectionPropTypes = {
  course: Course;
  offerings: OfferingAndSessions[];
  isSelected: (id: number) => boolean;
  onSelect: SelectCallback;
};

const descriptionProps = {
  mt: 4,
  mb: 4,
  fontSize: "sm",
  maxW: "45em",
};

const CourseSection = ({
  course,
  offerings,
  ...cardProps
}: CourseSectionPropTypes) => {
  const paragraphs = getParagraphs(course.description);
  return (
    <AccordionItem>
      <AccordionButton>
        <Box textAlign="left" fontSize="lg">
          {course.name}
        </Box>
        <Badge ml={3}>{offerings.length}</Badge>
        <Spacer />
        <AccordionIcon />
      </AccordionButton>
      <AccordionPanel pb={8}>
        <HStack>
          <Icon as={HiCurrencyDollar} position="relative" top="-1px" />
          <Text fontSize="sm">
            C${course.amountCents / 100} / month{" "}
            {course.taxType && `+ tax`}
          </Text>
        </HStack>
        <Text {...descriptionProps}>
          {paragraphs[0]}
        </Text>
        <Grid
          templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(3, 1fr)" }}
          gap={4}
          mt={6}
          mb={6}
        >
          {offerings.map((o) => (
            <OfferingCard key={o.id} {...cardProps} offering={o} />
          ))}
        </Grid>
        { paragraphs.slice(1).map(p => <Text key={p} {...descriptionProps}>{p}</Text>) }
      </AccordionPanel>
    </AccordionItem>
  );
};

type PropTypes = {
  onContinue: () => void;
  onMount?: () => void;
};

const SelectCourse = ({ onContinue, onMount }: PropTypes) => {
  const { offering: currentOffering, setOffering } = useRegistration();
  const { data, error } = useSWR("/sessions/trial");

  useEffect(() => {
    onMount && onMount();
  }, [onMount]);

  if (!data || error) return <></>; // TODO: loading state
  const offerings = camelify(data).offerings.map((o: any) => ({
    // TODO: memo
    ...o,
    sessions: o.sessions.map((s: any) => ({
      ...s,
      startsAt: parseISO(s.startsAt),
    })),
  })) as OfferingAndSessions[];

  const offeringsByCourse = groupBy(offerings, (o) => o.course.id);
  const courses = uniqBy(
    offerings.map((o) => o.course),
    (c) => c.id
  );

  const selectOffering = (offering?: OfferingAndSessions) => {
    if (!offering || offering.id === currentOffering?.id) {
      setOffering(undefined);
    } else {
      setOffering(offering);
    }
  };

  const resetOffering = () => selectOffering();

  return (
    <>
      <Heading size="md" mb={2}>
        Select a course
      </Heading>
      <Accordion allowToggle onChange={resetOffering}>
        {courses.sort(displayOrderPredicate).map((c) => (
          <CourseSection
            key={c.id}
            course={c}
            offerings={offeringsByCourse[c.id]}
            isSelected={(offeringId) => currentOffering?.id === offeringId}
            onSelect={selectOffering}
          />
        ))}
      </Accordion>

      <Box mt={{ base: 24, md: 0 }} />
      <BottomAction>
        {!currentOffering && (
          <Text m={2} textAlign="center" w="100%">
            Select a class to continue.
          </Text>
        )}
        {!!currentOffering && (
          <Button variant="primary" onClick={onContinue} w="100%">
            Continue
          </Button>
        )}
      </BottomAction>
    </>
  );
};

export default SelectCourse;
